'use strict';

const adformConstants = require('./adformConstants');
const { fetchPDPProductsSlider } = require('./adformBidsRequest');
const adformElementEvents = require('./adformElementEvents');
const initPopOver = require('progamers_core/components/initPopOver');
const { productTileTrackingInViewport } = require('progamers_core/components/datalayer');

/**
 * Init slider.
 * Note: no race condition with "swiperSlider.js":
 * - if "swiperSlider.js" runs before this, custom event "swiper:external-init" will be initialized => dispatchEvent below will init our slider.
 * - else => code below is ignored but slider will be initialized when "swiperSlider.js" runs (fetches all sliders on page)
 *
 * @param {Element} slider - slider to init
 */
function triggerInitSlider(slider) {
    const eventData = { detail: { slider: slider } };
    document.dispatchEvent(new CustomEvent('swiper:external-init', eventData));
}

/**
 * Returns element where we inject slider.
 * @return {Element} - slider wrapper
 */
function getSliderWrapper() {
    return document.querySelector('.js-adform-pdp-products-wrapper');
}

/**
 * Fetch products from our BE based on adform bids (brand_id...).
 * On success, add carousel on page and init events (in viewport, click...)
 * @param {Object[]} bids - array of bids
 * @param {string} pdpCategoryId - product category id
 */
function ajaxFetchProductsCarousel(bids, pdpCategoryId) {
    if (!bids || !bids.length) {
        return;
    }

    const ajaxSearchAdxCarouselPdpUrl = window.ajaxSearchAdxCarouselPdpUrl;
    let sliderWrapper = getSliderWrapper();
    const brandIds = bids.map(bid => bid.descriptionAsset.brand_id);
    const firstCategoryIdsPerBid = bids.map(bid => bid.descriptionAsset.category_ids[0]);

    if (!ajaxSearchAdxCarouselPdpUrl
        || !sliderWrapper
        || !pdpCategoryId
        || !brandIds.length
        || !firstCategoryIdsPerBid.length
    ) {
        return;
    }

    const params = [
        `brandIds=${brandIds.join('|')}`,
        `pdpCategoryId=${pdpCategoryId}`,
        `firstCategoryIdsPerBid=${firstCategoryIdsPerBid.join('|')}`
    ];

    fetch(`${ajaxSearchAdxCarouselPdpUrl}?${params.join('&')}`)
        .then((response) => {
            if (response.ok) {
                return response.text();
            }

            return false;
        })
        .then((html) => {
            if (!html) {
                return;
            }

            sliderWrapper.innerHTML = html;

            const slider = sliderWrapper.querySelector('.js-global-swiper-slider');
            if (!slider) {
                return;
            }

            triggerInitSlider(slider);

            document.body.dispatchEvent(new CustomEvent('product:tileRendered', { detail: slider, bubbles: true }));

            slider.querySelectorAll('.product').forEach(product => {
                const pdpURL = product.closest('[data-pdpURL]')?.getAttribute('data-pdpURL');
                const bidIndex = product.closest('[data-adformBidIndex]')?.getAttribute('data-adformBidIndex');
                const bid = bids[bidIndex];

                if (pdpURL && bid) {
                    adformElementEvents.initSponsoredProductAdInViewportObserver(product, bid);
                    adformElementEvents.initSponsoredProductAdClickEvent(product, bid, pdpURL);
                }
            });

            productTileTrackingInViewport(slider);

            initPopOver();
        });
}

/**
 * Init events that make adform request to get data needed for products slider.
 */
function initMakeRequestEvents() {
    document.addEventListener('DOMContentLoaded', () => {
        const pdpCategoryUrl = window.pdpCategoryUrl;
        let tagId = adformConstants.CATEGORIES_SPONSORED_PRODUCT_ADS_TAG_ID;
        if (!tagId || !pdpCategoryUrl || !getSliderWrapper()) {
            return;
        }

        fetchPDPProductsSlider(tagId, pdpCategoryUrl);
    }, { once: true }); // context: cookiebot may fire event again when partially accepted (since cookiebot not 1st script in head)
}

/**
 * Init events that wait for adform response to get processed:
 * - case bids exist => create slider with our products (search based on adform response)
 * - case no bids => nothing happens
 */
function initResponseHandlingEvents() {
    document.addEventListener(adformConstants.PDP_PRODUCTS_SLIDER_BIDS_READY_CUSTOM_EVENT_NAME, function (e) {
        const bids = e.detail?.bids;
        if (!bids.length) {
            return;
        }

        ajaxFetchProductsCarousel(bids, window.pdpCategoryId);
    });

    document.addEventListener(adformConstants.PDP_PRODUCTS_SLIDER_NO_BIDS_CUSTOM_EVENT_NAME, function () {});
}

/**
 * initResponseHandlingEvents() must be called before initMakeRequestEvents()
 * => otherwise request could dispatch event (e.g. early return "no-bids" event when request not valid)
 *    that can't be processed (response handler event not initialized yet)
 */
function initRequestLifecycleEvents() {
    initResponseHandlingEvents();
    initMakeRequestEvents();
}

module.exports = {
    init: function () {
        if (!window.shouldFetchAdformPDPProductsSlider
            || !window.ajaxSearchAdxCarouselPdpUrl
            || !window.pdpCategoryId
            || !window.pdpCategoryUrl
        ) {
            return;
        }

        initRequestLifecycleEvents();
    }
};
